import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  Send, Check, AlertTriangle, Clock, Calendar, ChevronDown, ChevronUp,
  Search, LayoutGrid, List, RefreshCw,
} from 'lucide-react';
import * as api from '../../../../services/api/timeReportingApi';
import * as S from '../../Common/internalToolsStyles';
import { format, eachDayOfInterval, isWeekend } from 'date-fns';

const LANES = [
  { id: 'open', label: 'Öppna', color: '#6b7280', bg: '#f3f4f6', icon: Clock },
  { id: 'submitted', label: 'Inskickade', color: '#d97706', bg: '#fffbeb', icon: Send },
  { id: 'approved', label: 'Godkända', color: '#059669', bg: 'rgba(5,150,105,0.06)', icon: Check },
  { id: 'rejected', label: 'Avvisade', color: '#dc2626', bg: 'rgba(239,68,68,0.06)', icon: AlertTriangle },
];

const weekdayLetters = ['M', 'T', 'O', 'T', 'F', 'L', 'S'];

// ─── MINI PERIOD CARD ───

const PeriodMiniCard = ({ period, isExpanded, onToggle, entries, loadingEntries }) => {
  const workMins = entries?.filter(e => !e.leave_type_id).reduce((s, e) => s + (e.duration_minutes || 0), 0) || 0;
  const leaveMins = entries?.filter(e => e.leave_type_id).reduce((s, e) => s + (e.duration_minutes || 0), 0) || 0;
  const workH = workMins / 60;
  const leaveH = leaveMins / 60;

  // Content tags — unique leave types and projects in this period
  const contentTags = useMemo(() => {
    if (!entries?.length) return [];
    const tags = [];
    const seenLeave = new Set();
    const seenProject = new Set();
    for (const e of entries) {
      if (e.leave_type_id && !seenLeave.has(e.leave_type_id)) {
        seenLeave.add(e.leave_type_id);
        const icons = { SEM: '🏖️', SJUK: '🏥', PARENT: '👶', VAB: '🧒', COMP: '⏰' };
        tags.push({ label: e.leave_type_name || 'Frånvaro', color: '#d97706', icon: icons[e.leave_type_code] });
      }
      if (e.project_id && !e.leave_type_id && !seenProject.has(e.project_id)) {
        seenProject.add(e.project_id);
        tags.push({ label: e.project_name || 'Projekt', color: e.project_color || 'var(--primary)' });
      }
    }
    return tags;
  }, [entries]);

  // Daily bar data
  const dailyHours = useMemo(() => {
    if (!entries?.length) return [];
    try {
      const days = eachDayOfInterval({
        start: new Date(period.period_start),
        end: new Date(period.period_end),
      });
      return days.map(day => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const mins = entries.filter(e => e.entry_date === dayStr).reduce((s, e) => s + (e.duration_minutes || 0), 0);
        return { date: day, hours: mins / 60 };
      });
    } catch (e) { return []; }
  }, [entries, period.period_start, period.period_end]);

  const maxH = Math.max(...dailyHours.map(d => d.hours), 8);

  // Project groups
  const projectGroups = useMemo(() => {
    if (!entries?.length) return [];
    const groups = {};
    for (const e of entries) {
      const key = e.leave_type_id ? `leave-${e.leave_type_id}` : (e.project_id || 'other');
      if (!groups[key]) {
        groups[key] = {
          name: e.project_name || e.leave_type_name || 'Övrigt',
          color: e.project_color || (e.leave_type_id ? '#f59e0b' : '#6b7280'),
          isLeave: !!e.leave_type_id,
          totalMinutes: 0,
        };
      }
      groups[key].totalMinutes += e.duration_minutes || 0;
    }
    return Object.values(groups).sort((a, b) => b.totalMinutes - a.totalMinutes);
  }, [entries]);

  return (
    <div className="period-mini-card">
      {/* Date + hours */}
      <div className="d-flex align-items-center justify-content-between mb-1">
        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem', color: 'var(--secondary)' }}>
          <Calendar size={11} style={{ opacity: 0.5 }} />
          <span>{format(new Date(period.period_start), 'd MMM')} – {format(new Date(period.period_end), 'd MMM')}</span>
        </div>
        <button className="period-expand-btn" onClick={() => onToggle(period.period_id)}>
          {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      {/* Hours summary */}
      <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.8125rem' }}>
        <span style={{ fontWeight: 700, color: 'var(--title)' }}>
          {period.total_hours ? `${Math.round(Number(period.total_hours))}h` : '0h'}
        </span>
        {isExpanded && entries?.length > 0 && workH > 0 && leaveH > 0 && (
          <span style={{ fontSize: '0.6875rem', color: 'var(--secondary)' }}>
            {workH.toFixed(workH % 1 ? 1 : 0)}h arbete · {leaveH.toFixed(leaveH % 1 ? 1 : 0)}h frånvaro
          </span>
        )}
      </div>

      {/* Content tags */}
      {contentTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {contentTags.map((tag, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: '0.5625rem', fontWeight: 500, padding: '1px 6px',
              borderRadius: 4, background: `${tag.color}11`, color: tag.color,
              border: `1px solid ${tag.color}22`,
              whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {tag.icon && <span style={{ fontSize: '0.5625rem' }}>{tag.icon}</span>}
              {tag.label}
            </span>
          ))}
        </div>
      )}

      {/* Rejection reason */}
      {period.rejection_reason && (
        <div style={{
          marginTop: 6, padding: '5px 8px', borderRadius: 5,
          background: 'rgba(239,68,68,0.06)', fontSize: '0.6875rem',
          color: '#dc2626', lineHeight: 1.3,
        }}>
          <AlertTriangle size={10} style={{ marginRight: 3, verticalAlign: -1 }} />
          {period.rejection_reason}
        </div>
      )}

      {/* Approved info */}
      {period.approved_at && (
        <div style={{ marginTop: 4, fontSize: '0.6875rem', color: 'var(--secondary)' }}>
          Godkänd {format(new Date(period.approved_at), 'd MMM yyyy')}
        </div>
      )}

      {/* ─── EXPANDED DETAIL ─── */}
      {isExpanded && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
          {loadingEntries ? (
            <div style={{ textAlign: 'center', padding: 8 }}>
              <Spinner animation="border" size="sm" variant="secondary" />
            </div>
          ) : !entries?.length ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--secondary)', textAlign: 'center', padding: 8 }}>
              Inga registreringar
            </div>
          ) : (
            <>
              {/* Mini bar chart */}
              <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 32, marginBottom: 8 }}>
                {dailyHours.map((d, i) => {
                  const barH = d.hours > 0 ? Math.max((d.hours / maxH) * 28, 2) : 0;
                  const isOff = isWeekend(d.date);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <div
                        style={{
                          width: '100%', maxWidth: 18, borderRadius: 2, height: barH || 1,
                          background: d.hours === 0 ? '#e5e7eb' : d.hours > 8 ? '#dc2626' : isOff ? '#f59e0b' : 'var(--primary)',
                          opacity: d.hours === 0 ? 0.25 : 1,
                        }}
                        title={`${format(d.date, 'd MMM')}: ${d.hours.toFixed(1)}h`}
                      />
                      <span style={{ fontSize: '0.5rem', color: isOff ? '#d1d5db' : 'var(--secondary)', lineHeight: 1 }}>
                        {weekdayLetters[i % 7]}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Project breakdown */}
              {projectGroups.map((g, i) => {
                const hours = g.totalMinutes / 60;
                const total = (period.total_hours || 1);
                const pct = Math.min((hours / total) * 100, 100);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 0', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, flex: 1 }}>
                      <div style={{ width: 6, height: 6, borderRadius: 2, background: g.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.6875rem', color: 'var(--title)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.name}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <div style={{ width: 36, height: 3, borderRadius: 2, background: '#f1f5f9', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: g.color }} />
                      </div>
                      <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--title)', minWidth: 24, textAlign: 'right' }}>
                        {hours.toFixed(hours % 1 ? 1 : 0)}h
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── BOARD COMPONENT ───

// Ordering for list view: status priority first (things needing user action
// bubble up), then most-recent period_start within each bucket.
const LIST_STATUS_ORDER = { rejected: 0, open: 1, submitted: 2, approved: 3, locked: 4 };

const MyPeriodsBoard = ({
  viewMode = 'kanban',
  onViewModeChange,
  refreshKey = 0,
  onRefresh,
}) => {
  const { companyId } = useParams();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contentFilter, setContentFilter] = useState('all'); // all, work, leave, or specific leave type
  const [expandedId, setExpandedId] = useState(null);
  const [entryCache, setEntryCache] = useState({});
  const [loadingEntries, setLoadingEntries] = useState(null);
  const [allEntriesLoaded, setAllEntriesLoaded] = useState(false);

  // Load periods, then batch-load all entries for filtering
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await api.getTimesheetPeriods(companyId, { limit: 50 });
        const loadedPeriods = res.data.periods || [];

        // Fetch entries for all periods in parallel. Period membership is now
        // derived from date range (no period_id FK on time_entry).
        const validPeriods = loadedPeriods.filter(p => ['open', 'submitted', 'approved', 'rejected'].includes(p.status));
        const entryResults = await Promise.all(
          validPeriods.map(p =>
            api.getTimeEntries(companyId, {
              employeeId: p.employee_id,
              periodStart: p.period_start,
              periodEnd: p.period_end,
            })
              .then(r => ({ id: p.period_id, entries: r.data.entries || [] }))
              .catch(() => ({ id: p.period_id, entries: [] })),
          ),
        );
        const cache = {};
        entryResults.forEach(r => { cache[r.id] = r.entries; });
        setEntryCache(cache);

        // total_hours is no longer stored on the period row — compute from entries.
        const enrichedPeriods = loadedPeriods.map(p => {
          const entries = cache[p.period_id] || [];
          const totalHours = Math.round(entries.reduce((s, e) => s + (e.duration_minutes || 0), 0) / 60 * 100) / 100;
          return { ...p, total_hours: totalHours };
        });
        setPeriods(enrichedPeriods);
        setAllEntriesLoaded(true);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [companyId, refreshKey]);

  const handleToggle = useCallback((periodId) => {
    setExpandedId(prev => prev === periodId ? null : periodId);
  }, []);

  // Extract unique project names and leave types across all entries for filter options
  const filterOptions = useMemo(() => {
    const projects = new Map(); // id -> name
    const leaveTypes = new Map(); // id -> name
    for (const entries of Object.values(entryCache)) {
      for (const e of entries) {
        if (e.project_id && e.project_name) projects.set(e.project_id, e.project_name);
        if (e.leave_type_id && e.leave_type_name) leaveTypes.set(e.leave_type_id, e.leave_type_name);
      }
    }
    return {
      projects: Array.from(projects, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
      leaveTypes: Array.from(leaveTypes, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [entryCache]);

  // Filter periods
  const filtered = useMemo(() => {
    let items = periods.filter(p => ['open', 'submitted', 'approved', 'rejected'].includes(p.status));

    // Status filter
    if (statusFilter !== 'all') items = items.filter(p => p.status === statusFilter);

    // Content filter — only keep periods that contain matching entries
    if (contentFilter !== 'all' && allEntriesLoaded) {
      items = items.filter(p => {
        const entries = entryCache[p.period_id] || [];
        if (contentFilter === 'work') return entries.some(e => e.project_id && !e.leave_type_id);
        if (contentFilter === 'leave') return entries.some(e => e.leave_type_id);
        // Specific project or leave type id
        if (contentFilter.startsWith('project:')) {
          const pid = contentFilter.slice(8);
          return entries.some(e => e.project_id === pid);
        }
        if (contentFilter.startsWith('leave:')) {
          const ltId = contentFilter.slice(6);
          return entries.some(e => e.leave_type_id === ltId);
        }
        return true;
      });
    }

    // Text search — match date, project names, or leave type names in entries
    if (searchTerm && allEntriesLoaded) {
      const q = searchTerm.toLowerCase();
      items = items.filter(p => {
        // Match date
        if (p.period_start?.includes(q) || p.period_end?.includes(q)) return true;
        // Match entry content
        const entries = entryCache[p.period_id] || [];
        return entries.some(e =>
          (e.project_name && e.project_name.toLowerCase().includes(q)) ||
          (e.project_code && e.project_code.toLowerCase().includes(q)) ||
          (e.leave_type_name && e.leave_type_name.toLowerCase().includes(q)) ||
          (e.description && e.description.toLowerCase().includes(q))
        );
      });
    } else if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(p => p.period_start?.includes(q) || p.period_end?.includes(q));
    }

    return items;
  }, [periods, searchTerm, statusFilter, contentFilter, entryCache, allEntriesLoaded]);

  // Per-lane recency filter for terminal statuses (approved/rejected).
  // Mirrors the supplier-invoices kanban: 30-day default keeps the lane
  // tidy without having to scroll through a year of past timesheets.
  const [approvedDays, setApprovedDays] = useState(30);
  const [rejectedDays, setRejectedDays] = useState(30);

  const { grouped, laneTotals } = useMemo(() => {
    const g = { open: [], submitted: [], approved: [], rejected: [] };
    for (const p of filtered) { if (g[p.status]) g[p.status].push(p); }
    for (const k of Object.keys(g)) g[k].sort((a, b) => b.period_start.localeCompare(a.period_start));
    const totals = {
      open: g.open.length,
      submitted: g.submitted.length,
      approved: g.approved.length,
      rejected: g.rejected.length,
    };
    const cutoffFromDays = (days) => {
      const c = new Date();
      c.setDate(c.getDate() - days);
      return c;
    };
    if (approvedDays > 0) {
      const cutoff = cutoffFromDays(approvedDays);
      g.approved = g.approved.filter(p => p.approved_at && new Date(p.approved_at) >= cutoff);
    }
    if (rejectedDays > 0) {
      const cutoff = cutoffFromDays(rejectedDays);
      g.rejected = g.rejected.filter(p => p.updated_at && new Date(p.updated_at) >= cutoff);
    }
    return { grouped: g, laneTotals: totals };
  }, [filtered, approvedDays, rejectedDays]);

  // Flat list for the Lista view — user wants rejected/open on top,
  // approved/locked at the bottom, newest first within each bucket.
  const listed = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const ao = LIST_STATUS_ORDER[a.status] ?? 99;
      const bo = LIST_STATUS_ORDER[b.status] ?? 99;
      if (ao !== bo) return ao - bo;
      return b.period_start.localeCompare(a.period_start);
    });
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { open: 0, submitted: 0, approved: 0, rejected: 0 };
    periods.forEach(p => { if (c[p.status] !== undefined) c[p.status]++; });
    return c;
  }, [periods]);

  if (loading) return null; // Don't show skeleton, just appear when ready
  if (periods.length === 0) return null; // Nothing to show

  return (
    <div style={{ marginTop: 20 }}>
      <style>{`
        .my-periods-board {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          overflow-x: auto;
          padding-bottom: 0.5rem;
        }
        .my-periods-lane {
          flex: 1;
          min-width: 0;
          background: #f8f9fb;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          max-height: 420px;
          min-height: 180px;
          transition: all 0.2s ease;
        }
        .my-periods-lane.lane-empty {
          background: transparent;
          border: 2px dashed #b0b8c4;
          min-height: 120px;
          opacity: 0.6;
        }
        .my-periods-lane.lane-empty .mp-lane-header {
          background: transparent;
        }
        .mp-lane-header {
          padding: 0.75rem 0.875rem;
          background: white;
          border-radius: 12px 12px 0 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .mp-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 12px;
        }
        .mp-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 5px 12px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 999px;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #111827;
          cursor: pointer;
          transition: all 0.15s;
        }
        .mp-pill:hover { background: #f3f4f6; }
        .mp-pill.active {
          background: var(--rgba-primary-2);
          color: var(--bs-primary);
          border-color: var(--bs-primary);
          font-weight: 600;
        }
        .mp-pill-count {
          font-size: 0.6875rem;
          padding: 1px 6px;
          border-radius: 999px;
          background: #e5e7eb;
          color: #374151;
          font-weight: 600;
        }
        .mp-pill.active .mp-pill-count {
          background: var(--bs-primary);
          color: white;
        }
        .mp-lane-date-filter {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          padding: 0.25rem 0.375rem;
          font-size: 0.6875rem;
          color: var(--secondary);
          background: #f8f9fb;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          outline: none;
        }
        .mp-lane-date-filter:focus {
          border-color: var(--primary);
        }
        .mp-lane-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          border-radius: 10px;
          font-size: 0.6875rem;
          font-weight: 600;
          padding: 0 0.375rem;
        }
        .mp-lane-cards {
          padding: 0.375rem;
          overflow-y: auto;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }
        .mp-lane-cards::-webkit-scrollbar { width: 4px; }
        .mp-lane-cards::-webkit-scrollbar-track { background: transparent; }
        .mp-lane-cards::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
        .mp-lane-empty-text {
          text-align: center;
          padding: 1.25rem 0.5rem;
          color: var(--secondary);
          font-size: 0.6875rem;
          opacity: 0.5;
        }
        .period-mini-card {
          background: white;
          border: 1px solid #e8ecf1;
          border-radius: 8px;
          padding: 0.625rem 0.75rem;
          transition: all 0.15s ease;
        }
        .period-mini-card:hover {
          border-color: var(--primary);
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        }
        .period-expand-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px; height: 20px;
          border-radius: 4px;
          border: none;
          background: #f3f4f6;
          color: var(--secondary);
          cursor: pointer;
          padding: 0;
          transition: all 0.15s ease;
        }
        .period-expand-btn:hover {
          background: var(--rgba-primary-1);
          color: var(--primary);
        }
        .mp-board-search {
          position: relative;
        }
        .mp-board-search input {
          padding-left: 2rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.8125rem;
          height: 34px;
          width: 220px;
          transition: all 0.2s ease;
          background: white;
        }
        .mp-toolbar-search input { width: 240px; }
        @media (max-width: 640px) {
          .mp-toolbar-search { flex: 1; min-width: 0; }
          .mp-toolbar-search input { width: 100%; }
        }
        .mp-board-search input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
          outline: none;
        }
        .mp-board-search .mp-search-icon {
          position: absolute;
          left: 0.625rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--secondary);
          opacity: 0.4;
        }

        /* List-view table — shares the tokenized feel of .proj-table */
        .tr-history-table {
          width: 100%; border-collapse: collapse; font-size: 13px;
        }
        .tr-history-table th {
          padding: 10px 16px; font-size: 11px; font-weight: 500;
          color: #6b7280; text-transform: uppercase; letter-spacing: 0.03em;
          text-align: left; background: #f9fafb;
          border-bottom: 1px solid #f1f5f9;
        }
        .tr-history-table th.num { text-align: right; }
        .tr-history-table td {
          padding: 10px 16px; border-bottom: 1px solid #f1f5f9;
          vertical-align: middle; color: #374151;
        }
        .tr-history-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
        .tr-history-table tr:last-child td { border-bottom: none; }
        .tr-history-table tr:hover td { background: #f9fafb; }
        .tr-history-table .week-cell {
          font-weight: 700; color: #111827; font-variant-numeric: tabular-nums;
        }
        /* Unified toolbar — search + filters + view toggle + refresh */
        .mp-toolbar {
          display: flex; align-items: center; gap: 8px;
          margin-bottom: 12px; flex-wrap: wrap;
        }
        .mp-toolbar-spacer { flex: 1; min-width: 0; }
        .mp-toolbar-select {
          height: 34px; padding: 0 28px 0 10px;
          border: 1px solid #e2e8f0; border-radius: 8px;
          background: white; color: #374151;
          font-size: 0.8125rem; line-height: 1;
          appearance: none; -webkit-appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
          background-repeat: no-repeat;
          background-position: right 10px center;
          cursor: pointer; outline: none;
          transition: border-color 150ms, box-shadow 150ms;
        }
        .mp-toolbar-select:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(68,129,78,0.12); }

        .mp-view-toggle {
          display: inline-flex; gap: 2px;
          background: #f3f4f6; border-radius: 8px; padding: 3px;
          height: 34px; align-items: center;
        }
        .mp-view-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; height: 28px; border-radius: 6px; border: none;
          background: transparent; color: #6b7280; cursor: pointer;
          transition: all 120ms ease;
        }
        .mp-view-btn:hover { color: #111827; }
        .mp-view-btn.active {
          background: white; color: #111827;
          box-shadow: 0 1px 2px rgba(0,0,0,0.06);
        }

        .mp-refresh-btn {
          display: inline-flex; align-items: center; gap: 6px;
          height: 34px; padding: 0 12px; border-radius: 8px;
          border: 1px solid #e5e7eb; background: white; color: #374151;
          font-size: 0.8125rem; font-weight: 500; cursor: pointer;
          transition: background 120ms ease;
        }
        .mp-refresh-btn:hover { background: #f9fafb; }

        /* Subtle left-edge hint for status */
        .tr-history-row td:first-child { border-left: 3px solid transparent; }
        .tr-history-row.status-rejected  td:first-child { border-left-color: #dc2626; }
        .tr-history-row.status-open      td:first-child { border-left-color: #6b7280; }
        .tr-history-row.status-submitted td:first-child { border-left-color: #d97706; }
        .tr-history-row.status-approved  td:first-child { border-left-color: #059669; }
        .tr-history-row.status-locked    td:first-child { border-left-color: #64748b; }
      `}</style>

      {/* Unified toolbar — search, filters, view toggle, refresh all on
          one row so they align under the section title on the parent page. */}
      <div className="mp-toolbar">
        <div className="mp-board-search mp-toolbar-search">
          <Search size={13} className="mp-search-icon" />
          <input
            type="text"
            placeholder="Sök datum, projekt, frånvaro..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="mp-toolbar-spacer" />

        {onViewModeChange && (
          <div className="mp-view-toggle" role="tablist" aria-label="Vy">
            <button
              role="tab"
              aria-selected={viewMode === 'kanban'}
              aria-label="Kanban"
              className={`mp-view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => onViewModeChange('kanban')}
              title="Kanban"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              role="tab"
              aria-selected={viewMode === 'list'}
              aria-label="Lista"
              className={`mp-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => onViewModeChange('list')}
              title="Lista"
            >
              <List size={14} />
            </button>
          </div>
        )}
        {onRefresh && (
          <button type="button" className="mp-refresh-btn" onClick={onRefresh} title="Uppdatera">
            <RefreshCw size={14} /> Uppdatera
          </button>
        )}
      </div>

      {/* Content pills — Arbete / Frånvaro plus dynamic leave-type and
          project chips. Same pattern as ApprovalPage so the two boards
          stay visually consistent. */}
      <div className="mp-pills">
        <button
          type="button"
          className={`mp-pill ${contentFilter === 'all' ? 'active' : ''}`}
          onClick={() => setContentFilter('all')}
        >
          Allt innehåll
        </button>
        <button
          type="button"
          className={`mp-pill ${contentFilter === 'work' ? 'active' : ''}`}
          onClick={() => setContentFilter('work')}
        >
          Arbete
        </button>
        <button
          type="button"
          className={`mp-pill ${contentFilter === 'leave' ? 'active' : ''}`}
          onClick={() => setContentFilter('leave')}
        >
          Frånvaro
        </button>
        {filterOptions.leaveTypes.map(lt => (
          <button
            key={`lt-${lt.id}`}
            type="button"
            className={`mp-pill ${contentFilter === `leave:${lt.id}` ? 'active' : ''}`}
            onClick={() => setContentFilter(`leave:${lt.id}`)}
          >
            {lt.name}
          </button>
        ))}
        {filterOptions.projects.map(p => (
          <button
            key={`pr-${p.id}`}
            type="button"
            className={`mp-pill ${contentFilter === `project:${p.id}` ? 'active' : ''}`}
            onClick={() => setContentFilter(`project:${p.id}`)}
          >
            {p.name}
          </button>
        ))}
      </div>

      {/* Status pills — single source of truth for the status filter,
          mirrors the pattern used in ApprovalPage so the two boards feel
          like the same product. Each pill shows its unfiltered count. */}
      <div className="mp-pills">
        <button
          type="button"
          className={`mp-pill ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          Alla
          <span className="mp-pill-count">
            {counts.open + counts.submitted + counts.approved + counts.rejected}
          </span>
        </button>
        {[
          { id: 'open', label: 'Öppna' },
          { id: 'submitted', label: 'Inskickade' },
          { id: 'approved', label: 'Godkända' },
          { id: 'rejected', label: 'Avvisade' },
        ].map(s => (
          <button
            key={s.id}
            type="button"
            className={`mp-pill ${statusFilter === s.id ? 'active' : ''}`}
            onClick={() => setStatusFilter(s.id)}
          >
            {s.label}
            {counts[s.id] > 0 && <span className="mp-pill-count">{counts[s.id]}</span>}
          </button>
        ))}
      </div>

      {/* Kanban lanes */}
      {viewMode === 'kanban' && (
        <div className="my-periods-board">
          {LANES.filter(lane => {
            // Avvisade is rare and visually noisy when empty — hide it
            // unconditionally for the read-only employee view. It reappears
            // automatically when a manager rejects a period.
            if (lane.id !== 'rejected') return true;
            return (grouped.rejected || []).length > 0;
          }).map(lane => {
            const items = grouped[lane.id] || [];
            const isEmpty = items.length === 0;
            const LaneIcon = lane.icon;

            return (
              <div key={lane.id} className={`my-periods-lane${isEmpty ? ' lane-empty' : ''}`}>
                <div className="mp-lane-header">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <LaneIcon size={13} style={{ color: isEmpty ? 'var(--secondary)' : lane.color }} />
                      <span style={{ fontWeight: 600, fontSize: '0.75rem', color: isEmpty ? 'var(--secondary)' : 'var(--title)' }}>
                        {lane.label}
                      </span>
                    </div>
                    {!isEmpty && (() => {
                      const isTerminal = lane.id === 'approved' || lane.id === 'rejected';
                      const days = lane.id === 'approved' ? approvedDays : rejectedDays;
                      const total = laneTotals[lane.id] || 0;
                      const showRatio = isTerminal && days > 0 && total > items.length;
                      return (
                        <span className="mp-lane-count" style={{ background: lane.bg, color: lane.color }}>
                          {showRatio ? `${items.length} av ${total}` : items.length}
                        </span>
                      );
                    })()}
                  </div>
                  {(lane.id === 'approved' || lane.id === 'rejected') && (
                    <select
                      className="mp-lane-date-filter"
                      value={lane.id === 'approved' ? approvedDays : rejectedDays}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (lane.id === 'approved') setApprovedDays(v);
                        else setRejectedDays(v);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value={7}>Senaste 7 dagar</option>
                      <option value={30}>Senaste 30 dagar</option>
                      <option value={90}>Senaste 90 dagar</option>
                      <option value={0}>Visa alla</option>
                    </select>
                  )}
                </div>
                <div className="mp-lane-cards">
                  {isEmpty ? (
                    <div className="mp-lane-empty-text">
                      {lane.id === 'rejected' ? 'Inga avvisade' : `Inga ${lane.label.toLowerCase()}`}
                    </div>
                  ) : (
                    items.map(p => (
                      <PeriodMiniCard
                        key={p.period_id}
                        period={p}
                        isExpanded={expandedId === p.period_id}
                        onToggle={handleToggle}
                        entries={entryCache[p.period_id]}
                        loadingEntries={loadingEntries === p.period_id}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view — actionable (rejected/open) on top, done at bottom */}
      {viewMode === 'list' && (
        listed.length === 0 ? (
          <div style={{
            padding: '32px 20px', textAlign: 'center',
            color: S.colors.textLight, fontSize: 13,
            background: 'white', border: `1px solid ${S.colors.borderLight}`, borderRadius: 10,
          }}>
            Inga perioder matchar filtret.
          </div>
        ) : (
          <div style={{ background: 'white', border: `1px solid ${S.colors.borderLight}`, borderRadius: 10, overflow: 'hidden' }}>
            <table className="tr-history-table">
              <thead>
                <tr>
                  <th>Vecka</th>
                  <th>Period</th>
                  <th className="num">Timmar</th>
                  <th>Status</th>
                  <th>Innehåll</th>
                  <th>Senaste händelse</th>
                </tr>
              </thead>
              <tbody>
                {listed.map(p => {
                  const entries = entryCache[p.period_id] || [];
                  const workMins = entries.filter(e => !e.leave_type_id).reduce((s, e) => s + (e.duration_minutes || 0), 0);
                  const leaveMins = entries.filter(e => e.leave_type_id).reduce((s, e) => s + (e.duration_minutes || 0), 0);
                  const projSet = new Set();
                  const leaveSet = new Set();
                  for (const e of entries) {
                    if (e.project_id && !e.leave_type_id) projSet.add(e.project_name || 'Projekt');
                    if (e.leave_type_id) leaveSet.add(e.leave_type_name || 'Frånvaro');
                  }
                  const contentBits = [];
                  if (projSet.size > 0) contentBits.push(`${projSet.size} projekt`);
                  if (leaveSet.size > 0) contentBits.push([...leaveSet].join(', '));
                  const weekStart = new Date(p.period_start);
                  const weekNum = format(weekStart, 'w');
                  const lastEvent = p.status === 'approved' && p.approved_at
                    ? `Godkänd ${format(new Date(p.approved_at), 'd MMM')}`
                    : p.status === 'rejected' && p.rejection_reason
                    ? `Avvisad — ${p.rejection_reason}`
                    : p.status === 'submitted' && p.submitted_at
                    ? `Inskickad ${format(new Date(p.submitted_at), 'd MMM')}`
                    : '—';

                  return (
                    <tr key={p.period_id} className={`tr-history-row status-${p.status}`}>
                      <td className="week-cell">v{weekNum}</td>
                      <td>
                        {format(new Date(p.period_start), 'd MMM')} – {format(new Date(p.period_end), 'd MMM yyyy')}
                      </td>
                      <td className="num">
                        <span style={{ fontWeight: 700, color: S.colors.textPrimary }}>
                          {(workMins + leaveMins) > 0 ? `${Math.round((workMins + leaveMins) / 60 * 10) / 10}h` : '0h'}
                        </span>
                        {workMins > 0 && leaveMins > 0 && (
                          <span style={{ fontSize: 11, color: S.colors.textLight, marginLeft: 6 }}>
                            ({Math.round(workMins / 60)}h arb · {Math.round(leaveMins / 60)}h frånv)
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={S.statusBadge[p.status] || S.statusBadge.open}>
                          {S.statusLabel[p.status] || p.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: S.colors.textMuted }}>
                        {contentBits.length > 0 ? contentBits.join(' · ') : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: S.colors.textLight }} title={p.rejection_reason || ''}>
                        {lastEvent.length > 60 ? lastEvent.slice(0, 57) + '…' : lastEvent}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default MyPeriodsBoard;
